/**
 * Copyright-confirmation gating for video creation.
 *
 * The blocking "confirm this is your own content" dialog only makes sense
 * when the audio could plausibly be someone else's work: an uploaded file or
 * an external platform link (YouTube/TikTok/direct MP3). Songs generated in
 * Solmi, Suno links, and our own demo tracks skip it — the user (or Solmi)
 * made that audio, so the confirmation is pure friction there.
 *
 * Once a user confirms once, we remember it per account and stop re-asking.
 */

export type AudioOrigin =
  | "solmi_song"
  | "suno"
  | "demo"
  | "upload"
  | "youtube"
  | "tiktok"
  | "soundcloud"
  | "link"
  | "unknown";

export function isSunoAudioUrl(url: string | null | undefined): boolean {
  if (!url) return false;
  try {
    const withProtocol = /^[a-z][a-z\d+\-.]*:\/\//i.test(url)
      ? url
      : `https://${url}`;
    const host = new URL(withProtocol).hostname.toLowerCase();
    return host === "suno.com" || host.endsWith(".suno.com") || host.endsWith(".suno.ai") || host === "suno.ai";
  } catch {
    return false;
  }
}

/** Origins exempt from the copyright dialog. "unknown" (e.g. restored drafts) stays gated. */
export function audioOriginRequiresCopyrightConfirmation(
  origin: AudioOrigin,
): boolean {
  return origin !== "solmi_song" && origin !== "suno" && origin !== "demo";
}

const ACK_KEY_PREFIX = "solmi_copyright_ack_v1:";

function ackKeyFor(userId?: string | null): string {
  return `${ACK_KEY_PREFIX}${userId || "anon"}`;
}

export function hasAcknowledgedCopyright(userId?: string | null): boolean {
  if (typeof window === "undefined") return false;
  try {
    return !!window.localStorage.getItem(ackKeyFor(userId));
  } catch {
    return false;
  }
}

export function markCopyrightAcknowledged(userId?: string | null): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(ackKeyFor(userId), new Date().toISOString());
  } catch {
    // Storage unavailable (private mode etc.) — the dialog just shows again.
  }
}
